import { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import type { LabelReadingDTO, ShelfDTO, UploadedPhotoDTO } from '@adega/shared';
import { ApiError } from '../api/client.js';
import { CamposDoVinho } from '../components/CamposDoVinho.js';
import { CellarGrid } from '../components/CellarGrid.js';
import { AiConfigBanner, ErrorBox, Loading } from '../components/Feedback.js';
import { PageHeader } from '../components/PageHeader.js';
import {
  useAiStatus,
  useAuth,
  useCellar,
  useCellars,
  useCreateBottle,
  useReadLabel,
  useUploadPhoto,
} from '../api/queries.js';
import {
  FORMULARIO_VAZIO,
  aplicarLeituraNoFormulario,
  dadosDoFormulario,
  type Formulario,
} from '../lib/formularioGarrafa.js';
import {
  RETOMAR_LEITURA_MS,
  apagarRascunho,
  chaveDoRascunho,
  fotosDaLeitura,
  gravarRascunho,
  lerRascunho,
  type FotoSalva,
} from '../lib/rascunhoGarrafa.js';

type Sugestao = NonNullable<LabelReadingDTO['sugestao']>;

/**
 * Cadastro de garrafa em duas etapas: a foto sobe e fica guardada, e só então a
 * IA lê o rótulo a partir dela. Tudo o que a pessoa já fez (fotos, campos,
 * posição, leitura em andamento) fica num rascunho na aba — ver
 * `lib/rascunhoGarrafa.ts` para o motivo.
 */
export function AddBottle() {
  const navigate = useNavigate();
  const { data: auth } = useAuth();
  const { data: cellars } = useCellars();
  const { data: aiStatus } = useAiStatus();
  const upload = useUploadPhoto();
  const ler = useReadLabel();
  const criar = useCreateBottle();

  // Tocar no ＋ de uma posição livre na tela da adega chega aqui com
  // ?adega=&prateleira=&posicao= já escolhidos.
  const [parametros] = useSearchParams();
  const numeroDoParametro = (nome: string) => {
    const valor = Number(parametros.get(nome));
    return parametros.has(nome) && Number.isInteger(valor) && valor >= 0 ? valor : undefined;
  };
  const adegaDoLink = numeroDoParametro('adega');
  const prateleiraDoLink = numeroDoParametro('prateleira');
  const posicaoDoLink = numeroDoParametro('posicao');
  const vagaDoLink =
    prateleiraDoLink !== undefined && posicaoDoLink !== undefined
      ? { shelfId: prateleiraDoLink, position: posicaoDoLink }
      : null;

  const chaveRascunho = auth?.user ? chaveDoRascunho(auth.user.id) : null;
  // Lido uma única vez, na montagem.
  const [recuperado] = useState(() => (chaveRascunho ? lerRascunho(chaveRascunho) : null));
  const [avisoRecuperado, setAvisoRecuperado] = useState(recuperado !== null);

  // O link vence o rascunho: quem tocou numa vaga específica quer aquela vaga.
  const [cellarId, setCellarId] = useState<number | null | undefined>(
    () => adegaDoLink ?? recuperado?.cellarId ?? undefined,
  );
  const [vagaEscolhida, setVaga] = useState<{ shelfId: number; position: number } | null>(() => {
    if (vagaDoLink) return vagaDoLink;
    const mesmaAdega = adegaDoLink === undefined || adegaDoLink === recuperado?.cellarId;
    return mesmaAdega ? (recuperado?.vaga ?? null) : null;
  });

  /**
   * Três estados diferentes, e confundi-los foi o bug: `undefined` é "ainda não
   * escolheu", `null` é "sem adega, vai para a fila", e um número é a adega.
   *
   * Assumir a primeira adega em silêncio mandava a garrafa para a adega errada.
   * E exigir uma adega seria pior: com as adegas cheias, a vaga vai abrir onde a
   * pessoa beber, e amarrar a garrafa a um móvel agora é decidir cedo demais.
   */
  const semAdega = cellarId === null;
  const idEfetiva =
    cellarId === undefined ? (cellars?.length === 1 ? cellars[0]?.id : undefined) : (cellarId ?? undefined);
  const precisaEscolherAdega = (cellars?.length ?? 0) > 1 && cellarId === undefined;
  /** Quem ainda não cadastrou móvel nenhum: a garrafa nasce sem adega e pronto. */
  const semNenhumaAdega = (cellars?.length ?? 0) === 0;
  const { data: cellar, isLoading } = useCellar(idEfetiva);

  // Um input para câmera e outro para galeria, com um "alvo" dizendo se a foto
  // escolhida é a frente ou o contrarrótulo.
  const inputCamera = useRef<HTMLInputElement>(null);
  const inputGaleria = useRef<HTMLInputElement>(null);
  const alvoFoto = useRef<'frente' | 'verso'>('frente');

  const [form, setForm] = useState<Formulario>(recuperado?.form ?? FORMULARIO_VAZIO);
  const [fotoSalva, setFotoSalva] = useState<FotoSalva | null>(recuperado?.fotoSalva ?? null);
  const [confianca, setConfianca] = useState<number | null>(recuperado?.confianca ?? null);
  const [leituraConcluida, setLeituraConcluida] = useState<string | null>(
    recuperado?.leituraConcluida ?? null,
  );
  const [leituraPendente, setLeituraPendente] = useState(recuperado?.leituraPendente ?? null);
  /**
   * Leitura em curso nesta página. Existe além de `ler.isPending` porque este
   * muda um instante depois do disparo; sem ele o botão "Ler o rótulo" piscaria
   * entre o fim do upload e o início da leitura.
   */
  const [lendo, setLendo] = useState(false);

  /**
   * Campos que a pessoa alterou à mão. Uma nova leitura da IA (ao adicionar o
   * contrarrótulo ou trocar a frente) nunca sobrescreve esses campos: correção
   * humana vale mais que releitura automática.
   *
   * Ref e não estado: a leitura leva segundos, e a pessoa pode editar nesse
   * intervalo — o valor precisa estar atual no momento em que a resposta chega.
   */
  const camposEditados = useRef(new Set<keyof Formulario>(recuperado?.camposEditados ?? []));

  // Cada mudança relevante vai para o rascunho da aba.
  useEffect(() => {
    if (!chaveRascunho) return;
    gravarRascunho(chaveRascunho, {
      form,
      camposEditados: [...camposEditados.current],
      fotoSalva,
      confianca,
      cellarId: cellarId ?? null,
      vaga: vagaEscolhida,
      leituraConcluida,
      leituraPendente,
    });
  }, [chaveRascunho, form, fotoSalva, confianca, cellarId, vagaEscolhida, leituraConcluida, leituraPendente]);

  const aplicarSugestao = (s: Sugestao) => {
    setConfianca(s.confidence);
    // A conversão mora em `lib/formularioGarrafa.ts` porque a consulta de
    // rótulo usa a mesma ao transformar uma consulta em garrafa.
    setForm((atual) => aplicarLeituraNoFormulario(atual, s, camposEditados.current));
  };

  /** Etapa 2: a IA lê as fotos já guardadas. Pode ser repetida sem novo upload. */
  const lerRotulo = async (fotos: FotoSalva) => {
    const chave = fotosDaLeitura(fotos);
    setLendo(true);
    setConfianca(null);
    setLeituraPendente({ fotos: chave, desde: Date.now() });

    try {
      const resultado = await ler.mutateAsync({
        photoKey: fotos.photoKey,
        backPhotoKey: fotos.backPhotoKey,
      });
      setLeituraConcluida(chave);
      setLeituraPendente(null);
      // Sem IA a foto fica salva e o usuário preenche à mão — o fluxo não trava.
      if (resultado.sugestao) aplicarSugestao(resultado.sugestao);
    } catch (erro) {
      // O erro aparece na tela (ler.error), com botão para tentar de novo.
      // Se o servidor respondeu, a leitura acabou — não há o que retomar. Falha
      // de rede é diferente: o servidor pode ter concluído, e retomar é de graça.
      if (!(erro instanceof ApiError) || erro.status !== 0) {
        setLeituraPendente(null);
      }
    } finally {
      setLendo(false);
    }
  };

  // Voltou de um recarregamento com leitura no meio: retoma sozinho, uma vez.
  const retomada = useRef(false);
  useEffect(() => {
    if (!aiStatus || retomada.current) return;
    retomada.current = true;

    if (!recuperado?.leituraPendente) return;
    const { leituraPendente: pendente, fotoSalva: fotos } = recuperado;

    if (
      fotos &&
      aiStatus.enabled &&
      pendente.fotos === fotosDaLeitura(fotos) &&
      Date.now() - pendente.desde < RETOMAR_LEITURA_MS
    ) {
      void lerRotulo(fotos);
    } else {
      // Antiga demais para ser de graça: fica o botão, a pessoa decide.
      setLeituraPendente(null);
    }
    // Só reage à chegada do status da IA; o resto vem do rascunho lido na montagem.
  }, [aiStatus]);

  /** Etapa 1: sobe só a foto escolhida. O verso não reenvia a frente. */
  const enviarFoto = async (arquivo: File, alvo: 'frente' | 'verso') => {
    ler.reset();

    let enviada: UploadedPhotoDTO;
    try {
      enviada = await upload.mutateAsync(arquivo);
    } catch {
      return; // upload.error aparece na tela
    }

    const novas: FotoSalva =
      alvo === 'verso' && fotoSalva
        ? { ...fotoSalva, backPhotoKey: enviada.photoKey }
        : {
            photoKey: enviada.photoKey,
            thumbKey: enviada.thumbKey,
            url: enviada.photoUrl,
            backPhotoKey: fotoSalva?.backPhotoKey ?? null,
          };

    setFotoSalva(novas);
    setConfianca(null);

    if (aiStatus?.enabled) {
      await lerRotulo(novas);
    }
  };

  const escolherFoto = (alvo: 'frente' | 'verso', origem: 'camera' | 'galeria') => {
    alvoFoto.current = alvo;
    (origem === 'camera' ? inputCamera : inputGaleria).current?.click();
  };

  const receberFoto = (input: HTMLInputElement) => {
    const arquivo = input.files?.[0];
    // Limpa o input para que escolher o mesmo arquivo de novo dispare o evento.
    input.value = '';
    if (!arquivo) return;
    if (alvoFoto.current === 'verso' && !fotoSalva) return;
    void enviarFoto(arquivo, alvoFoto.current);
  };

  const recomecar = () => {
    if (chaveRascunho) apagarRascunho(chaveRascunho);
    camposEditados.current.clear();
    setForm(FORMULARIO_VAZIO);
    setFotoSalva(null);
    setConfianca(null);
    setLeituraConcluida(null);
    setLeituraPendente(null);
    setCellarId(adegaDoLink);
    setVaga(vagaDoLink);
    upload.reset();
    ler.reset();
    setAvisoRecuperado(false);
  };

  if (isLoading) return <Loading />;

  // A vaga só vale se a prateleira é da adega exibida e a posição continua livre
  // — um link antigo, um rascunho velho ou outra pessoa ocupando a vaga não pode
  // vingar calado.
  const prateleiraDaVaga = cellar?.shelves.find((s) => s.id === vagaEscolhida?.shelfId);
  const vagaLivre =
    vagaEscolhida && prateleiraDaVaga?.cells[vagaEscolhida.position]?.bottle === null;
  const vaga =
    vagaEscolhida && prateleiraDaVaga && vagaLivre
      ? {
          ...vagaEscolhida,
          rotulo: `${prateleiraDaVaga.label}, posição ${vagaEscolhida.position + 1}`,
        }
      : null;

  const definir = (campo: keyof Formulario, valor: string) => {
    camposEditados.current.add(campo);
    setForm((atual) => ({ ...atual, [campo]: valor }));
  };

  const selecionarVaga = (shelf: ShelfDTO, position: number) =>
    setVaga({ shelfId: shelf.id, position });

  const salvar = async () => {
    const criada = await criar.mutateAsync({
      ...dadosDoFormulario(form),
      photoKey: fotoSalva?.photoKey ?? null,
      thumbKey: fotoSalva?.thumbKey ?? null,
      backPhotoKey: fotoSalva?.backPhotoKey ?? null,
      cellarId: semAdega ? null : (idEfetiva ?? null),
      shelfId: vaga?.shelfId ?? null,
      position: vaga?.position ?? null,
    });

    if (chaveRascunho) apagarRascunho(chaveRascunho);
    navigate(`/garrafa/${criada.id}`);
  };

  const ocupado = upload.isPending || lendo || ler.isPending;
  const faltaLer =
    Boolean(aiStatus?.enabled) &&
    fotoSalva !== null &&
    leituraConcluida !== fotosDaLeitura(fotoSalva) &&
    !ocupado;

  /**
   * A leitura terminou e a safra não veio: o contrarrótulo resolveria.
   *
   * Governa duas coisas ao mesmo tempo, e por isso vive aqui em vez de inline:
   * a explicação acima dos botões, e o **destaque na Câmera** — com a garrafa
   * na mão, fotografar é o caminho; a galeria serve a quem já tinha a foto.
   */
  const precisaDoVerso =
    Boolean(aiStatus?.enabled) &&
    fotoSalva !== null &&
    !fotoSalva.backPhotoKey &&
    !faltaLer &&
    !ocupado &&
    form.vintage.trim() === '';

  return (
    <div className="space-y-4">
      <PageHeader titulo="Adicionar garrafa" subtitulo="Comece pela foto do rótulo" voltar />

      {avisoRecuperado ? (
        <div className="flex flex-wrap items-center justify-between gap-2 rounded-xl border border-adega-border bg-adega-surface px-4 py-3 text-sm">
          <p className="text-adega-muted">Recuperamos o cadastro que estava em andamento.</p>
          <div className="flex gap-2">
            <button type="button" className="btn-ghost text-xs" onClick={recomecar}>
              Começar do zero
            </button>
            <button
              type="button"
              className="btn-secondary text-xs"
              onClick={() => setAvisoRecuperado(false)}
            >
              Continuar
            </button>
          </div>
        </div>
      ) : null}

      <section className="card space-y-3">
        {/*
          Dois inputs e dois botões, de propósito. Com `capture` o Android abre só
          a câmera; sem ele, o Android recente abre só o seletor de fotos do
          sistema, que não tem câmera. Nenhum atributo único cobre os dois casos.
        */}
        <input
          ref={inputCamera}
          type="file"
          accept="image/*"
          capture="environment"
          className="hidden"
          onChange={(e) => receberFoto(e.currentTarget)}
        />
        <input
          ref={inputGaleria}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={(e) => receberFoto(e.currentTarget)}
        />

        {upload.isPending ? (
          <p className="py-3 text-center text-sm text-adega-muted">Enviando foto…</p>
        ) : lendo || ler.isPending ? (
          <p className="py-3 text-center text-sm text-adega-muted">
            Lendo o rótulo… pode levar alguns segundos.
          </p>
        ) : null}

        {fotoSalva ? (
          <div className="space-y-3">
            <div className="flex gap-3">
              <img
                src={fotoSalva.url}
                alt="Foto do rótulo"
                className="h-32 w-24 shrink-0 rounded-lg object-cover"
              />
              <div className="min-w-0 flex-1 space-y-1">
                <p className="text-sm text-adega-muted">
                  Foto salva{fotoSalva.backPhotoKey ? ' (frente e verso)' : ''}.
                </p>
                {confianca !== null ? (
                  <p className="text-xs text-adega-muted">
                    Confiança da leitura: {Math.round(confianca * 100)}%. Confira antes de salvar.
                  </p>
                ) : null}
              </div>
            </div>

            {faltaLer ? (
              <div className="space-y-1">
                <button
                  type="button"
                  className="btn-primary w-full py-3"
                  onClick={() => void lerRotulo(fotoSalva)}
                >
                  ✨ {ler.error ? 'Tentar ler de novo' : 'Ler o rótulo com IA'}
                </button>
                <p className="text-center text-xs text-adega-muted">
                  A leitura não terminou. A foto já está salva — não precisa enviar de novo.
                </p>
              </div>
            ) : null}

            {!fotoSalva.backPhotoKey ? (
              <div className="space-y-2">
                {/* A explicação vem ANTES dos botões: ler o motivo e então ver
                    a ação é a ordem natural. Embaixo, ela justificaria um toque
                    que a pessoa já teria dado ou ignorado. */}
                {precisaDoVerso ? (
                  <p className="rounded-xl border border-adega-gold/40 bg-adega-surface-2 p-3 text-sm leading-relaxed">
                    <strong className="font-semibold">A safra não veio no rótulo.</strong>{' '}
                    <span className="text-adega-muted">
                      Ela quase sempre está no contrarrótulo — e é de lá que a IA tira o que o
                      produtor declara sobre a guarda.
                    </span>
                  </p>
                ) : null}

                <EscolherFoto
                  titulo="Contrarrótulo"
                  /* Com a garrafa na mão, fotografar é o caminho; a galeria
                     serve a quem já tinha a foto. */
                  destaque={precisaDoVerso}
                  disabled={ocupado}
                  onCamera={() => escolherFoto('verso', 'camera')}
                  onGaleria={() => escolherFoto('verso', 'galeria')}
                />
              </div>
            ) : null}

            <button
              type="button"
              className="btn-ghost w-full text-xs"
              disabled={ocupado}
              onClick={() => escolherFoto('frente', 'galeria')}
            >
              Trocar foto da frente
            </button>
          </div>
        ) : (
          <EscolherFoto
            titulo={aiStatus?.enabled ? 'Foto do rótulo — a IA preenche o resto' : 'Foto do rótulo'}
            destaque
            disabled={ocupado}
            onCamera={() => escolherFoto('frente', 'camera')}
            onGaleria={() => escolherFoto('frente', 'galeria')}
          />
        )}


        <AiConfigBanner contexto="A foto é salva normalmente, mas os campos precisam ser preenchidos à mão." />
        {upload.error ? (
          <ErrorBox erro={upload.error} />
        ) : ler.error ? (
          <ErrorBox erro={ler.error} />
        ) : null}
      </section>

      <section className="card space-y-3">
        <h2 className="font-semibold">Dados do vinho</h2>
        <CamposDoVinho form={form} definir={definir} />
      </section>

      {/*
        Sem nenhuma adega cadastrada, não há posição que se possa escolher — e
        perguntar "onde vai ficar?" para quem guarda as garrafas num armário é
        pedir que ela invente um lugar que não existe. A garrafa nasce sem
        adega, e o dia em que a pessoa cadastrar um móvel ela move.
      */}
      {semNenhumaAdega ? null : (
      <section className="space-y-2">
        <div className="flex items-center justify-between">
          <h2 className="font-semibold">Onde vai ficar?</h2>
          {vaga ? (
            <button type="button" className="btn-ghost text-xs" onClick={() => setVaga(null)}>
              Limpar
            </button>
          ) : null}
        </div>

        {cellars && cellars.length > 0 ? (
          <div className="flex flex-wrap gap-1.5">
            {/* Com as adegas cheias, "nenhuma" é a resposta certa: a garrafa
                espera na fila e entra onde a vaga abrir. */}
            <button
              type="button"
              onClick={() => {
                setCellarId(null);
                setVaga(null);
              }}
              className={`rounded-full px-3 py-1 text-xs transition ${
                semAdega
                  ? 'bg-adega-wine text-white'
                  : 'border border-adega-border text-adega-muted hover:text-adega-text'
              }`}
            >
              Sem adega · fila
            </button>
            {cellars.map((item) => (
              <button
                key={item.id}
                type="button"
                onClick={() => {
                  setCellarId(item.id);
                  setVaga(null);
                }}
                className={`rounded-full px-3 py-1 text-xs transition ${
                  item.id === idEfetiva
                    ? 'bg-adega-wine text-white'
                    : 'border border-adega-border text-adega-muted hover:text-adega-text'
                }`}
              >
                {item.name}
              </button>
            ))}
          </div>
        ) : null}

        <p className="text-xs text-adega-muted">
          {semAdega
            ? 'Vai para a fila, sem ficar presa a uma adega: entra onde a próxima vaga abrir.'
            : precisaEscolherAdega
              ? 'Escolha a adega, ou "Sem adega" se ela vai esperar vaga.'
              : vaga
                ? `Selecionado: ${vaga.rotulo}`
                : 'Toque em uma posição livre. Pode deixar sem posição e definir depois.'}
        </p>

        {cellar ? (
          <CellarGrid
            cellar={cellar}
            onSelecionarVaga={selecionarVaga}
            vagasSelecionadas={vaga ? [{ shelfId: vaga.shelfId, position: vaga.position }] : []}
          />
        ) : null}
      </section>
      )}

      {criar.error ? <ErrorBox erro={criar.error} /> : null}

      {/*
        Fixo acima da barra de navegação e dizendo o destino. Antes o botão ficava
        no fim de 7 prateleiras, longe do "Selecionado: …", e a pessoa chegava nele
        sem saber se a posição tinha sido marcada.
      */}
      <div className="sticky bottom-[calc(4rem+env(safe-area-inset-bottom)+0.5rem)] z-10 -mx-1 rounded-2xl border border-adega-border bg-adega-bg/95 p-2 backdrop-blur">
        <button
          type="button"
          className="btn-primary w-full py-3"
          disabled={criar.isPending || form.name.trim().length === 0 || precisaEscolherAdega}
          onClick={() => void salvar()}
        >
          {criar.isPending
            ? 'Salvando…'
            : precisaEscolherAdega
              ? 'Escolha a adega'
              : semAdega
                ? 'Salvar na fila'
                : vaga
                  ? `Salvar em ${vaga.rotulo}`
                  : semNenhumaAdega
                    ? 'Salvar garrafa'
                    : 'Salvar sem posição'}
        </button>
        {!criar.isPending && (form.name.trim().length === 0 || precisaEscolherAdega) ? (
          <p className="pt-1.5 text-center text-xs text-adega-muted">
            {form.name.trim().length === 0
              ? fotoSalva
                ? 'Confira o nome do vinho para salvar.'
                : 'Tire a foto do rótulo para começar.'
              : 'Toque no nome da adega em "Onde vai ficar?".'}
          </p>
        ) : null}
      </div>
    </div>
  );
}

function EscolherFoto({
  titulo,
  destaque = false,
  disabled,
  onCamera,
  onGaleria,
}: {
  titulo: string;
  destaque?: boolean;
  disabled: boolean;
  onCamera: () => void;
  onGaleria: () => void;
}) {
  return (
    <div className="space-y-2">
      <p className="text-sm font-medium">{titulo}</p>
      <div className="grid grid-cols-2 gap-2">
        <button
          type="button"
          className={destaque ? 'btn-primary py-3' : 'btn-secondary py-3'}
          disabled={disabled}
          onClick={onCamera}
        >
          📷 Câmera
        </button>
        <button type="button" className="btn-secondary py-3" disabled={disabled} onClick={onGaleria}>
          🖼️ Galeria
        </button>
      </div>
    </div>
  );
}
